/**
 * FilterSetRow — a single row in the PlusMenu dropdown.
 * Shows name + filter count chip; tooltip appears only on filter count hover.
 * If the current user is the owner, shows an edit icon on the right.
 */
import React from 'react';
import { Button, Tooltip, Typography } from 'antd';
import type { SavedFilterSet } from '@/pages/Shipments/ControlTower/types';
import { useTranslation } from 'react-i18next';
import { buildSavedFilterTooltip } from '@/pages/Shipments/ControlTower/utils/filterPresetTooltip';
import styles from '../TabStrip.module.scss';
import { TruncateWithTooltip } from '@/components';
import { PencilIcon } from '@/assets/icons/icons';

const { Text } = Typography;

interface Props {
  filterSet: SavedFilterSet;
  onClick: (id: string) => void;
  onEdit?: (filterSet: SavedFilterSet) => void;
}

const FilterSetRow: React.FC<Props> = ({ filterSet, onClick, onEdit }) => {
  const { t } = useTranslation('shipments');
  const tooltipText = buildSavedFilterTooltip(filterSet.appliedFilters ?? []);
  const filterCount = filterSet.filterCount ?? 0;
  const isOwner = Boolean(filterSet.isOwner);

  return (
    <div
      data-testid={`filter-set-row-${filterSet.id}`}
      onClick={() => onClick(filterSet.id)}
      className={styles.filterSetRow}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(filterSet.id);
        }
      }}
    >
      <div className={styles.filterSetRowLeft}>
        <TruncateWithTooltip
          text={filterSet.name}
          max={25}
          render={(value) => (
            <span className={styles.filterSetRowName}>{value}</span>
          )}
        />

        {filterCount > 0 && tooltipText ? (
          <Tooltip
            title={
              <div className='saved-filter-tooltip-content'>
                {tooltipText.split('\n').map((line, index) => (
                  // eslint-disable-next-line react/no-array-index-key -- static split of one tooltip string, never reordered
                  <span key={index}>{line}</span>
                ))}
              </div>
            }
            mouseEnterDelay={0.4}
            mouseLeaveDelay={0}
            placement='right'
            overlayClassName='saved-filter-tooltip'
            overlayStyle={{ pointerEvents: 'none' }}
            destroyTooltipOnHide
          >
            <span>
              <Text type='secondary' className={styles.filterSetRowCount}>
                {t('tabs.filterCount_other', { count: filterCount })}
              </Text>
            </span>
          </Tooltip>
        ) : (
          <Text type='secondary' className={styles.filterSetRowCount}>
            {t('tabs.filterCount_other', { count: filterCount })}
          </Text>
        )}
      </div>

      {isOwner && onEdit && (
        <Button
          type='text'
          size='small'
          icon={
            <PencilIcon width={10} height={10} color='var(--text-primary)' />
          }
          className={styles.filterSetRowEditButton}
          aria-label={t('tabs.editFilterSet')}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(filterSet);
          }}
        />
      )}
    </div>
  );
};

export default FilterSetRow;
